<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../../../helpers/interface_helpers';
	import { getErrorOrInfoMessageForMinAndMax } from '../../../../helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	//Stores
		//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Input, Text } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	$: settingsStore = getDeviceSettings(id);
	export let device;

	/**
	 * Get the settings for the given device
	 * @param {Device} device - the device to get the settings for
	 * @returns {DeviceSettingsG2} the settings for the device
	 */
	const getSettings = (device: Device): DeviceSettingsG2 => {
		const id = real_id(device.id);
		return $settingsStore;
	};

	const id = real_id(device.id);
	let settings = getSettings(device);

	/**
	 * The minimum value for the reaction delay
	 */
	const min = 0;

	/**
	 * The maximum value for the reaction delay
	 */
	const max = 60;

	/**
	 * The current value of the reaction delay
	 */
	let reactionDelay = settings?.[get_gen_2_component_channel_structure(device)]?.reaction_delay;

	/**
	 * The cached value of the reaction delay
	 */
	let cacheDelay = reactionDelay;

	/**
	 * Whether the component is currently loading
	 */
	let loading = false;

	/**
	 * Function to send a request to the server to update the reaction delay
	 */
	const sendRequest = async () => {
		if (reactionDelay.length == 0) return;
		loading = true;
		const params = {
			id: device.channel,
			config: { id: device.channel, reaction_delay: +reactionDelay },
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			params,
		);

		if (res.isok) {
			settings[`${get_gen_2_component_channel_structure(device)}`].reaction_delay = reactionDelay;
			devices_settings.update_devices_settings(id, settings);
			cacheDelay = reactionDelay;
		} else {
			reactionDelay = cacheDelay;
		}
		onSettingsResponse(res, device);
		loading = false;
	};

	/**
	 * Get the error or info message for the reaction delay
	 */
	$: reactionDelayData = getErrorOrInfoMessageForMinAndMax(reactionDelay, min, max);

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: sendRequest,
				loading,
				disabled: loading || reactionDelay == cacheDelay || reactionDelay.length == 0,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType="body-small" class="mb-xs">
	{$format('heading_reaction_delay', { default: 'Reaction Delay' })}
</Text>

<Input
	class="mb-s"
	size="m"
	bind:value={reactionDelay}
	type={'number'}
	min={0}
	max={60}
	error={reactionDelayData.error}
	info={reactionDelayData.info}
	label={$format('min')}
	step={0.01}
	on:enter={sendRequest}
	id="input-rw1z3do"
	aria-labelledby="input-rw1z3do"
/>
{#if !$showNewLayout}
	<Button
		class="mt-s"
		{loading}
		disabled={loading || reactionDelay == cacheDelay || reactionDelay.length == 0}
		size="m"
		width="full"
		on:click={sendRequest}
	>
		{$format('button_save')}</Button
	>
{/if}
